import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter, Search, Toolbar, VirtualScroll } from '@syncfusion/ej2-react-grids';
import ReactMarkdown from "react-markdown";

import { useAuth } from '../contexts/AuthContext';

import ListDoxExt from '../components/ListDoxExt';
import ListDoc from '../components/ListDoc';
import ListDommage from '../components/ListDommage';
import ListIntervenant from '../components/ListIntervenant';
import FinanceTab from '../components/FinanceTab';
import Messagerie from '../components/Sidebar/Messagerie';
import GanttDeclaration from '../components/GanttDeclaration';

import ExternalLinksCard from '../components/ExternalLinksCard';

import GpaFormToPDF from '../components/Declaration/GpaFormToPDF';
import ActionsTab from '../components/Declaration/ActionsTab';

import useFetchDeclarations from '../hooks/useFetchDeclarations';
import useFetchSites from '../hooks/useFetchSites';
import useFetchDoxExt from '../hooks/useFetchDoxExt';
import useFetchDommages from '../hooks/useFetchDommages';
import useFetchDocs from '../hooks/useFetchDocs';
import useFetchIntervenants from '../hooks/useFetchIntervenants';
import useFetchLiensExternes from '../hooks/useFetchLiensExternes';
import useSectionVisibility from '../hooks/useSectionVisibility';

import { FileManagerProvider } from '../contexts/FileManagerContext';

import { sanitizeText } from '../utils/SanitizeText';
import { formatDate, formatCurrency, formatHtmlToText } from '../utils/Format';

import Loader from '../components/Loader';
import SectionVisibilityPanel from '../components/SectionVisibilityPanel';

import SectionCard from '../utils/SectionCard';
import InfoItem from '../utils/InfoItem';

function RefRow({ label, societe, nom, refcode }) {
	return (
		<div className="items-center gap-4 grid grid-cols-1 sm:grid-cols-12 py-3 border-b last:border-b-0">
			<div className="sm:col-span-3">
				<div className="text-gray-500 text-xs">{label}</div>
			</div>
			<div className="sm:col-span-3">
				<div className="font-medium text-sm">{societe || <span className="text-gray-400">—</span>}</div>
			</div>
			<div className="sm:col-span-3">
				<div className="font-medium text-sm">{nom || <span className="text-gray-400">—</span>}</div>
			</div>
			<div className="sm:col-span-3">
				<div className="font-mono font-medium text-sm">
					{refcode || <span className="text-gray-400">—</span>}
				</div>
			</div>
		</div>
	);
}

function DefautTemplate(props) {
	if (props.Archiver_Enr) {
		return (<div>
			<i className="text-blue-500 fa-square-check fas"></i>
		</div>);
	} else {
		return (<div>
			<i className="w-15 h-15 text-blue-500 fa-square far"></i>
		</div>);
	}
}

const statusTemplate = (props) => {
	const normalizedStatus = props.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

	const statusColors = {
		'garanti': 'bg-green-500',
		'non garanti': 'bg-red-500',
		'en attente': 'bg-yellow-500',
		'pre-rapport': 'bg-blue-500',
		'devis valide': 'bg-purple-500',
		'ouvert': 'bg-green-500',
		'sans suite': 'bg-red-700',
	};

	let statusColor = 'bg-white';
	for (const [key, value] of Object.entries(statusColors)) {
		if (normalizedStatus.startsWith(key)) {
			statusColor = value;
			break;
		}
	}

	return (
		<span className={`px-2 py-1 rounded text-white ${statusColor}`}>
			{props.Etat}
		</span>
	);
};

const DeclarationDetail = () => {
	const { hasRole } = useAuth();

	const { uuid } = useParams();

	const { declaration, fetchDeclarationByUUID, loading: loadingDecla } = useFetchDeclarations();
	const { site: siteDeclaration, fetchSiteByUUID, loading: loadingSite } = useFetchSites();
	const { doxExtByUUID, fetchDoxExtByUUID } = useFetchDoxExt();
	const { dommages, fetchDommagesByDeclaUUID, loading: loadingDommage } = useFetchDommages();
	const { lstDocs, fetchDocsByTypeAndDeclaUUID } = useFetchDocs();
	const { declarationIntervenants, fetchDeclaIntByDeclaUUID } = useFetchIntervenants();

	const { liensExts, fetchLiensExtsByUUIDAndOrigine, saveLiensExternesDiff, loading: loadingLiens, mutating } = useFetchLiensExternes();

	const [previewOpen, setPreviewOpen] = useState(false);

	const sections = [
		{ key: 'refs', label: 'Références' },
		{ key: 'lese', label: 'Lésé / GPA' },
		{ key: 'juridique', label: 'Juridique' },
		{ key: 'financier', label: 'Récapitulatif financier' },
		{ key: 'remarques', label: 'Remarques' },
		{ key: 'liens', label: 'Liens externes' },
	];

	const storageKey = `decla_sections_${uuid}`;

	const vis = useSectionVisibility(storageKey, sections);

	const [activeTab, setActiveTab] = useState('coord');


	useEffect(() => {
		if (uuid) {
			fetchDeclarationByUUID(uuid);
			fetchDoxExtByUUID(uuid);
			fetchDommagesByDeclaUUID(uuid);
			fetchDeclaIntByDeclaUUID(uuid);
			fetchDocsByTypeAndDeclaUUID(uuid, "Expertise");
			fetchLiensExtsByUUIDAndOrigine(uuid, "Declaration");
		}
	}, [uuid]);

	useEffect(() => {
		if (declaration?.UUID_Site_) {
			fetchSiteByUUID(declaration.UUID_Site_);
		}
	}, [declaration]);

	useEffect(() => {
		if (activeTab === 'finances') {
			fetchDommagesByDeclaUUID(uuid);
		}
		if (activeTab === 'docs') {
			refreshDoxExt();
		}
	}, [activeTab]);

	const refreshDoxExt = () => {
		fetchDoxExtByUUID(uuid);
	};

	const refreshDommages = () => {
		fetchDommagesByDeclaUUID(uuid);
	};

	const refreshDeclaration = () => {
		fetchDeclarationByUUID(uuid);
	};

	const refreshIntervenants = () => {
		fetchDeclaIntByDeclaUUID(uuid);
	};

	// if (!siteDeclaration) {
	// 	return <div class="flex flex-1 justify-center items-center"><Loader /></div>;
	// }

	const columnsDommage = [
		{ field: "Etat", headerText: "État", width: "150px", template: statusTemplate },
		{ field: "Etat_Motif", headerText: "Motif", width: "300px" },
		{ field: "Libelle", headerText: "Libelle", width: "300px" },
		{ field: "Libelle_Court", headerText: "Libelle_Court", width: "300px" },
		{ field: "Defaut", headerText: "Défaut", width: "100px", template: DefautTemplate, textAlign: "center" },
		{ field: "Cd_Patho_1", headerText: "Ouvrage", width: "150px" },
		{ field: "Cd_Patho_2", headerText: "Cause_Dommage", width: "150px" },
		{ field: "Cd_Patho_3", headerText: "Fait Générateur", width: "150px" },
		{ field: "_Date_Modif", headerText: "Modifié le", type: "date", format: "dd/MM/yyyy", width: "130px" },
		{ field: "_Date_Crea", headerText: "Créé le", type: "date", format: "dd/MM/yyyy", width: "130px" },
		{ field: "Quitus", headerText: "Quitus",width: "180px" },
	];

	const ALL_TABS = [
		{ id: 'coord', label: 'Coordonnées' },
		//{ id: 'histo', label: 'Historique', exclude: ['visiteur'] },
		{ id: 'actions', label: 'Actions' },
		{ id: 'dommages', label: 'Dommages' },
		{ id: 'exp', label: 'Expertises', exclude: ['visiteur'] },
		{ id: 'inter', label: 'Intervenants', exclude: ['visiteur'] },
		{ id: 'finances', label: 'Infos Financières', exclude: ['visiteur'] },
		/*{ id: 'quitus', label: 'Quitus' },*/
		{ id: 'conv', label: 'Conversations' },
		{ id: 'docs', label: 'Documents Externes' },
	];

	const canSee = (tab) => {
		if (tab.only) return tab.only.some((r) => hasRole(r));
		if (tab.exclude) return !tab.exclude.some((r) => hasRole(r));
		return true;
	};

	const visibleTabs = ALL_TABS.filter(canSee);

	const safeActiveTab = visibleTabs.some(t => t.id === activeTab)
		? activeTab
		: (visibleTabs[0]?.id ?? null);

	// console.log("Declaration chargé: UUID:", uuid, declaration);
	// console.log("Site chargé: UUID:",declaration.UUID_Site_,siteDeclaration);
	// console.log("Dommages chargé: UUID:",uuid,dommages);
	// console.log(intervenants);
	// console.log("Selected Row:", selectedRow);

	return (

		<div class="flex-1 p-4 lg:p-8">
			<div class="gap-6 grid grid-cols-12">
				<div class="gap-6 grid grid-cols-12 col-span-12 xxl:col-span-9">
					<div class="col-span-12">
						<div class="flex items-center bg-gray-900 drop-shadow-xl mb-6 px-8 rounded-lg w-full h-20">
							<div id="url" class="flex flex items-center gap-x-2 font-semibold text-gray-400 text-sm sm:text-lg">
								<Link to="/sites" class="hover:text-white decoration-orange-600 hover:underline hover:underline-offset-2 transition hover:translate-y-[-2px] duration-300">Sites</Link>
								<span class="cursor-default">/</span>
								<Link
									to={"/sites/" + siteDeclaration?.UUID_}
									className="hover:text-orange-600 transition hover:translate-y-[-2px] duration-300"
								>
									{loadingSite ? (
										<span className="inline-block bg-gray-200 rounded-2xl w-32 h-4 align-middle animate-pulse" />
									) : (
										<span className="text-center">{siteDeclaration?.Nom}</span>
									)}
								</Link>

								<span class="cursor-default">/</span>

								<Link to="/declarations" class="hover:text-white decoration-orange-600 hover:underline hover:underline-offset-2 transition hover:translate-y-[-2px] duration-300">Declarations</Link>

								<span class="cursor-default">/</span>

								<Link to="" class="block text-orange-600 transition hover:translate-y-[-2px]">
									{loadingDecla ? (
										<span className="inline-block bg-gray-200 rounded-2xl w-32 h-4 align-middle animate-pulse" />
									) : (
										<span className="text-center">{declaration?.Libelle ? `${declaration?.Libelle.slice(0, 40)}${declaration?.Libelle.length > 40 ? '...' : ''}` : 'Sans Libelle'}</span>
									)}
								</Link>
							</div>
						</div>

						<div className="mx-auto w-full">
							<div className="mb-4 border-gray-200 border-b">
								<ul className="flex flex-wrap -mb-px" role="tablist">
									{visibleTabs.map((tab) => (
										<li key={tab.id} className="mr-2" role="presentation">
											<button
												className={`text-base font-semibold text-center py-4 px-4 rounded-t-lg border-b-2
            											${safeActiveTab === tab.id
														? 'text-orange-600 border-orange-300'
														: 'text-orange-400 border-transparent hover:text-orange-500'}
            											transition duration-300 ease-in-out`}
												onClick={() => setActiveTab(tab.id)}
												type="button"
												role="tab"
												aria-controls={tab.id}
												aria-selected={safeActiveTab === tab.id}
											>
												{tab.label}
											</button>
										</li>
									))}
								</ul>
							</div>

							<div id="myTabContent">
								{activeTab === 'coord' && (
									<div id="form-info" className="space-y-6 mx-auto">
										{/* Header bandeau */}
										<div className="flex justify-between items-center bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 px-8 rounded-lg w-full min-h-20 text-white">
											<div className="flex items-center gap-10">
												<p className="flex items-center gap-4 py-1 h-8 font-bold text-2xl">
													<i className="fa-solid fa-file-shield fa-sm" />
													<span>{"Détails de la Déclaration"}</span>
												</p>

												<p className="space-x-2 font-medium text-sm">
													<i className="fa-solid fa-hashtag" />
													<span className="uppercase">{declaration?.Code_Procedure ?? (<span className="inline-block bg-gray-200 rounded-2xl w-28 h-3 align-middle animate-pulse" />)}</span>
													<span className="font-bold">({declaration?.Type_Declaration ?? (<span className="inline-block bg-gray-200 rounded-2xl w-12 h-3 align-middle animate-pulse" />)})</span>
												</p>

												<p className="space-x-2 font-medium text-sm">
													<i className="fa-solid fa-building" />
													<span>{siteDeclaration?.Nom ?? (<span className="inline-block bg-gray-200 rounded-2xl w-28 h-3 align-middle animate-pulse" />)}</span>
													<span className="font-bold">
														({siteDeclaration?.Ville ?? (<span className="inline-block bg-gray-200 rounded-2xl w-28 h-3 align-middle animate-pulse" />)}
														{siteDeclaration?.CP ? `, ${siteDeclaration?.CP}` : ""})
													</span>
												</p>
											</div>

											<div className="flex items-center gap-2">
												<button
													type="button"
													title="Aperçu / Voir"
													aria-label="Aperçu"
													disabled={loadingDecla}
													onClick={() => setPreviewOpen((v) => !v)}
													className="inline-flex justify-center items-center bg-white/10 hover:bg-white/20 disabled:opacity-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/60 w-9 h-9 transition-colors"
												>
													<i className="fa-solid fa-eye-low-vision" />
												</button>

												{/* Paramètres (engrenage) */}
												<button
													type="button"
													title="Paramètres"
													aria-label="Paramètres"
													disabled={loadingDecla}
													onClick={() => {
														// TODO: ouvre un drawer/modal paramètres
														// setOpenSettings(true)
													}}
													className="group inline-flex justify-center items-center bg-white/10 hover:bg-white/20 disabled:opacity-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/60 w-9 h-9 transition-colors"
												>
													<i className="group-hover:rotate-[20deg] transition-all duration-300 fa-solid fa-gear" />
												</button>
											</div>

										</div>

										{/* Panneau visibilité des sections */}
										<SectionVisibilityPanel
											open={previewOpen}
											onClose={() => setPreviewOpen(false)}
											sections={sections}
											storageKey={storageKey}
											floating
										/>

										{/* Corps en grid (indépendant, réordonnable) */}
										<div className="gap-3 grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 grid-flow-row-dense">
											<div className="col-span-1 2xl:col-span-2">
												<SectionCard title="Informations générales" icon="fa-circle-info" loading={loadingDecla}>
													<div className="space-y-3">
														<div className="space-y-1">
															<div className="text-gray-500 text-xs">Libellé Déclaration</div>
															<div className="font-medium text-sm whitespace-pre-wrap">
																{declaration?.Libelle || <span className="text-gray-400">—</span>}
															</div>
														</div>
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Type" value={declaration?.Type_Declaration} />
															<InfoItem label="Code" value={declaration?.Code_Procedure} mono />
															<InfoItem label="État" value={declaration?.Etat} />
															<InfoItem label="État Motif" value={declaration?.Etat_Motif} />
															<InfoItem label="Création" value={formatDate(declaration?._Date_Crea)} />
															<InfoItem label="Orig. demande" value={declaration?.Origine_de_la_demande} />
															<InfoItem label="Demande de revue par" value={declaration?.Demande_Revue_Par} />
															<InfoItem label="Suivi par" value={declaration?.Charge_affaire} />
															<InfoItem label="Date de sinistre" value={formatDate(declaration?.Date_Sinistre)} />
															<InfoItem label="Date de Déclaration" value={formatDate(declaration?.Date_Declaration)} />
														</div>
													</div>
												</SectionCard>
											</div>

											{/* 2) Références */}
											{vis.refs && (
												<div className="col-span-2 2xl:col-span-2">
													<SectionCard title="Références" icon="fa-address-book" loading={loadingDecla}>
														<div className="hidden gap-4 grid sm:grid grid-cols-12 mb-1 text-gray-500 text-xs">
															<div className="col-span-3" />
															<div className="col-span-3 text-center">Société</div>
															<div className="col-span-3 text-center">Nom & Prénom</div>
															<div className="col-span-3 text-center">Référence</div>
														</div>

														<RefRow label="Expert" societe={declaration?.Expert_Societe} nom={declaration?.Expert_Nom} refcode={declaration?.Expert_Ref} />
														<RefRow label="Assureur Client" societe={declaration?.Assureur_Client_Nom} nom={declaration?.Assureur_Client_Nom2} refcode={declaration?.Assureur_Client_Ref} />
														<RefRow label="Courtier" societe={declaration?.Courtier_Nom} nom={declaration?.Courtier_Nom2} refcode={declaration?.Courtier_Ref} />
														<RefRow label="Assureur Interne" societe={declaration?.Assureur_Interne_Nom} nom={declaration?.Assureur_Interne_Nom2} refcode={declaration?.Assureur_Interne_Ref} />
													</SectionCard>
												</div>
											)}

											{/* 3) Lésé / GPA */}
											{vis.lese && (
												<div className="col-span-3 2xl:col-span-2">
													<SectionCard title="Lésé / GPA" icon="fa-user" loading={loadingDecla}>
														<div className="gap-6 grid grid-cols-1 lg:grid-cols-2">
															{/* Col gauche */}
															<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
																<InfoItem label="Catégorie" value={declaration?.GPA_Type_Declarant} />
																<InfoItem label="OS" value={declaration?.GPA_OS} />
																<InfoItem label="Nom" value={declaration?.GPA_Locataire} />
																<InfoItem label="Prénom" value={declaration?.GPA_Prenom} />
																<InfoItem label="Societe" value={declaration?.GPA_Societe} />
																<InfoItem label="Adresse" value={declaration?.GPA_Adresse} />
																<InfoItem label="Comp" value={declaration?.GPA_App_Etage} />
																<InfoItem label="Télephone" value={declaration?.GPA_Locataire_Tel} />
																<InfoItem label="Email" value={declaration?.GPA_Email} />
															</div>
															{/* Col droite */}
															<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
																<InfoItem label="Saisie par" value={declaration?.GPA_Demande_Par} />
																<InfoItem label="Entreprise" value={declaration?.GPA_Intervenant} />
																<InfoItem label="Date RDV" value={formatDate(declaration?.GPA_Date_RDV)} />
																<InfoItem label="Date Limite" value={formatDate(declaration?.GPA_DateLimite)} />
																<InfoItem label="Sous un délai de" value={declaration?.GPA_NB_Jour_Inter} />
																<InfoItem label="Mise en demeure" value={formatDate(declaration?.GPA_Mise_en_demeure)} />
																<InfoItem label="Logement" value={declaration?.GPA_Num_Logement} />
																<InfoItem label="Relance soutraitant" value={declaration?.GPA_Relance_SousTraitant} />
																<InfoItem label="Bâtiment" value={declaration?.GPA_Num_Batiment} />
																<InfoItem label="Courrier locataire" value={declaration?.GPA_Courrier_Locataire} />
															</div>
														</div>
													</SectionCard>
												</div>
											)}

											{/* 4) Juridique */}
											{vis.juridique && (
												<div className="col-span-3 2xl:col-span-2">
													<SectionCard title="Juridique" icon="fa-scale-balanced" loading={loadingDecla}>
														<div className="gap-6 grid grid-cols-1 lg:grid-cols-2">
															{/* Col gauche */}
															<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
																<InfoItem label="Juriste" value={declaration?.Jur_Juriste} />
																<InfoItem label="Instances" value={declaration?.Jur_instance} />
																<InfoItem label="Num DOSSIER" value={declaration?.Jur_Num_Dossier_Avocat} mono />
																<InfoItem label="Avocat" value={declaration?.Jur_Avocat} />
																<InfoItem label="Parties" value={declaration?.Jur_Parties} />
																<InfoItem label="Suivi Tech" value={declaration?.Jur_Suivi_Tech} />
																<InfoItem label="Prétention" value={declaration?.Jur_Pretention} />
																<InfoItem label="Evaluation" value={declaration?.Jur_Evaluation} />
																<InfoItem label="Montant (€)" value={declaration?.Jur_Reel} />
																<InfoItem label="Réf. partie adverse" value={declaration?.Jur_Ref_partie_adverse} />
															</div>
															{/* Col droite */}
															<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
																<InfoItem label="Date Jugement" value={formatDate(declaration?.Jur_Date_Jugement)} />
																<InfoItem label="Date Assignation" value={formatDate(declaration?.Jur_Date_Assignation)} />
																<InfoItem label="Réf expert adverse" value={declaration?.Jur_Ref_expert_adverse} />
																<div className="hidden sm:block" />
																<div className="hidden sm:block" />
															</div>
														</div>
													</SectionCard>
												</div>
											)}

											{/* 5) Récapitulatif financier */}
											{vis.financier && (
												<div className="col-span-1">
													<SectionCard title="Récapitulatif financier" icon="fa-file-invoice-dollar" loading={loadingDecla}>
														<div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
															<InfoItem label="Coût Pathologie" value={formatCurrency(declaration?.Cout_tt_Pathologie)} />
															<InfoItem label="Coût Déclaration" value={formatCurrency(declaration?.Montant_Du_Definitif)} />
														</div>
													</SectionCard>
												</div>
											)}

											{/* 6) Remarques */}
											{vis.remarques && (
												<div className="col-span-1">
													<SectionCard title="Remarques" icon="fa-note-sticky" loading={loadingDecla} >
														<div className="space-y-1">
															<div className="text-gray-500 text-xs">Remarques</div>
															<div className="font-medium text-sm whitespace-pre-wrap">
																{declaration?.Remarques ? (
																	<ReactMarkdown>{formatHtmlToText(declaration.Remarques)}</ReactMarkdown>
																) : (
																	<span className="text-gray-400">—</span>
																)}
															</div>
														</div>
													</SectionCard>
												</div>
											)}

											{/* 7) Commentaires */}
											{/*{vis.commentaires && (
												<div className="col-span-1">
													<SectionCard title="Commentaires" icon="fa-note-sticky" loading={loadingDecla} >
														<div className="space-y-1">
															<div className="text-gray-500 text-xs">Commentaires</div>
															<div className="font-medium text-sm whitespace-pre-wrap">
																{declaration?.Commentaires ? (
																	<ReactMarkdown>{formatHtmlToText(declaration.Commentaires)}</ReactMarkdown>
																) : (
																	<span className="text-gray-400">—</span>
																)}
															</div>
														</div>
													</SectionCard>
												</div>
											)}*/}

											{/* 8) Liens externes */}
											{vis.liens && (
												<div className="lg:col-span-3 2xl:col-span-3">
													<ExternalLinksCard
														lstLiensExternes={liensExts}
														loading={loadingLiens}
														mutating={mutating}
														defaultUUIDExterne={uuid}
														defaultOrigine="Declaration"
														onBulkSave={(list) => saveLiensExternesDiff(uuid, "Declaration", list)}
													/>
												</div>
											)}
										</div>
									</div>
								)}
								{activeTab === 'histo' && (

									<div id="form-info" className="mx-auto">
										<div className="bg-white shadow-lg mb-6 p-4 md:p-8 px-4 rounded-lg">
											<GanttDeclaration />
										</div>
									</div>
								)}
								{activeTab === 'actions' && (

									<div id="form-info" className="mx-auto">
										<ActionsTab
											declaration={declaration}
											intervenants={declarationIntervenants}
											onCreate={(payload) => {
												console.log('CREATE ACTION =>', payload);
											}}
										/>
									</div>
								)}
								{activeTab === 'dommages' && (
									<ListDommage lstDommages={dommages} columns={columnsDommage}
									/>
								)}
								{activeTab === 'exp' && (
									<ListDoc lstDocs={lstDocs} type="Expertise" />
								)}
								{activeTab === 'inter' && (
									<ListIntervenant lstIntervenants={declarationIntervenants} showAddButton={false} uuidDeclaration={declaration.UUID_} refreshIntervenants={refreshIntervenants} />
								)}
								{activeTab === 'finances' && (
									<FinanceTab lstDommages={dommages} declaration={declaration} refreshDommages={refreshDommages} refreshDeclaration={refreshDeclaration} />
								)}
								{activeTab === 'quitus' && (
									<div className="h-[calc(100vh-200px)]">
										<div className="flex flex-col bg-white bg-white shadow-lg shadow-lg mb-6 p-4 md:p-8 px-4 rounded-lg rounded-lg h-full overflow-auto">
											<GpaFormToPDF declaration={declaration} />
										</div>
									</div>
								)}
								{activeTab === 'conv' && (
									<div className="h-[calc(100vh-250px)]">
										<div className="flex flex-col bg-white bg-white shadow-lg shadow-lg mb-6 p-4 md:p-8 px-4 rounded-lg rounded-lg h-full overflow-hidden">
											<Messagerie Externe={["Declaration", declaration.UUID_]} />
										</div>
									</div>
								)}
								{activeTab === 'docs' && (
									<FileManagerProvider>
										<ListDoxExt lstDoxExt={doxExtByUUID} refreshDoxExt={refreshDoxExt} />
									</FileManagerProvider>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

	);

};

export default DeclarationDetail;